/**
 * VAULT marketing content — single source of truth for every public
 * product/feature page. Any status label pulls from `/api/public/feature-status`
 * so this file never hard-codes Live / Beta / Coming Soon.
 *
 * Content style guide:
 *   • Use Organise / Understand / Review / Know / Find / Share / Stay Ahead
 *   • Never use Revolutionary / Disruptive / World's Best / Bank-grade / Guaranteed
 *   • Never claim regulated financial / tax / legal / investment advice
 *   • Never state security promises that aren't actually implemented
 */

export const HERO_CTA = {
    primary:   { label: "Create Your VAULT", to: "/signup",  testid: "cta-primary" },
    secondary: { label: "Explore Demo",     to: "/demo",     testid: "cta-secondary" },
    together:  { label: "Explore VAULT Together", to: "/together", testid: "cta-together" },
    feature:   { label: "See How It Works", to: "/product",  testid: "cta-feature" },
};

export const PRODUCT_PILLARS = [
    { key: "organise",   label: "Organise",   items: ["Cards & Accounts", "Expenses", "Payments", "Subscriptions", "Documents", "Assets", "Liabilities"] },
    { key: "understand", label: "Understand", items: ["Dashboard", "Money Map", "Net Worth", "Reports", "Financial Snapshots"] },
    { key: "stay_ahead", label: "Stay Ahead", items: ["VAULT Brief", "Financial Calendar", "Renewals", "Reminders", "Forward View"] },
    { key: "together",   label: "Together",   items: ["Couple Workspace", "Family Workspace", "Shared Records", "Joint Records", "Shared Goals", "Household Calendar"] },
    { key: "protect",    label: "Protect",    items: ["Privacy Controls", "Hide Values", "Permissions", "Workspace Security", "MFA", "Data Control"] },
    { key: "ask",        label: "Ask",        items: ["VAULT Intelligence", "Natural-language retrieval", "Document finding", "Attention queries"], gated: "vault_intelligence" },
];

const F = (statusKey, hero, sub, bullets, extras = {}) => ({ statusKey, hero, sub, bullets, ...extras });

// Feature pages — { statusKey: null | "beta" | "live" | "coming_soon" }
// null means the page renders without a status pill.
export const FEATURE_PAGES = {
    inbox: F("inbox", "Drop it into VAULT.",
        "Statement · Receipt · Policy · Invoice · Warranty · Investment Record · Loan Document — add it and we'll organise the rest.",
        ["Upload", "Recognise", "Review", "Confirm", "Organise"],
        { cta: "Add it to VAULT", ctaTo: "/signup" }),
    brief: F("brief", "Know what matters today.",
        "Today · This Week · This Month — a calm three-minute look at everything worth your attention.",
        ["Today", "This Week", "This Month"]),
    calendar: F("calendar", "See what's coming.",
        "Every date in your financial life, in one calm calendar.",
        ["Payments", "Renewals", "Subscriptions", "Documents", "Insurance", "Warranties"]),
    "money-map": F("money_map", "Your financial world, mapped.",
        "Five elegant tiles — Own, Owe, Moving, Coming, Attention — each deep-linked to the module that backs it.",
        ["Own", "Owe", "Moving", "Coming", "Attention"]),
    health: F("health", "How organised is your VAULT?",
        "The VAULT Health score measures how well organised your records are — never your wealth or your income.",
        ["Records reviewed", "Dates completed", "Reminders configured", "Security posture", "Reconciliation freshness"],
        { disclaimer: "This score measures organisation, not financial health. It is never a Credit Score, Financial Health Score, or Wealth Score." }),
    documents: F("documents", "Know where your important financial documents are.",
        "Policies, agreements, warranties, statements — organised, connected to the records they belong to, and never forgotten.",
        ["Upload", "Classify", "Organise", "Connect", "Review", "Remind"]),
    cards: F("cards", "Every card. One organised view.",
        "Every credit and debit card, organised with its documents, statements and utilisation history.",
        ["Card Master", "Due Dates", "Statements", "Payments", "Utilisation", "Documents"],
        { disclaimer: "VAULT never stores or displays CVV. We never request your PIN, OTP or CVV." }),
    subscriptions: F("subscriptions", "Know what keeps renewing.",
        "Monthly, annual and upcoming subscriptions — with duplicate detection and recorded price change flags.",
        ["Monthly", "Annual", "Upcoming", "Price History (when confirmed)", "Possible Duplicate", "Needs Review"]),
    investments: F("investments", "Keep your investment records organised.",
        "Record what you hold, when you bought it, and what you paid — VAULT never recommends what to buy.",
        ["Track", "Organise", "Record", "Review", "Report"],
        { disclaimer: "VAULT is not an investment adviser. We never recommend buying or selling, and we never claim to beat the market." }),
    assets: F("assets", "Your assets. Properly documented.",
        "From property to vehicles to collectibles and business assets — every asset with its papers.",
        ["Property", "Vehicle", "Collectibles", "Business Assets", "Other"]),
    liabilities: F("liabilities", "Know what you owe.",
        "Loans, EMIs, and other liabilities — informational only. No lending, no repayment automation.",
        ["Loans", "EMIs", "Other Liabilities"],
        { disclaimer: "Informational only. VAULT never moves money or takes repayment action on your behalf." }),
    reports: F("reports", "Turn organised information into a clearer picture.",
        "Net Worth, Obligations, Expenses, Subscriptions, Assets, Liabilities and Household reports.",
        ["Net Worth", "Obligations", "Expenses", "Subscriptions", "Assets", "Liabilities", "Household"]),
};

// Together pages
export const TOGETHER_HERO = {
    hero: "Financial life is better organised together.",
    sub:  "VAULT Together gives couples and families a shared financial workspace without forcing everything to become shared.",
    pillars: ["MY MONEY", "YOUR MONEY", "OUR MONEY"],
    ctas: [
        { label: "Explore Couples",  to: "/together/couples",  testid: "together-couples" },
        { label: "Explore Families", to: "/together/families", testid: "together-families" },
    ],
};

export const COUPLES_PAGE = {
    statusKey: "couples",
    hero: "One financial life. Two individual people.",
    sub:  "Individual logins, shared household, personal privacy. Together when you want it, private when you need it.",
    bullets: ["Individual logins", "Shared household", "Personal privacy", "Joint records",
              "Household obligations", "Shared goals", "Financial calendar"],
    tabs: ["Together", "Mine", "Partner", "Joint"],
};

export const FAMILIES_PAGE = {
    statusKey: "families",
    hero: "Keep the family's financial world organised.",
    sub:  "Documents, obligations, insurance, subscriptions and important dates — one calm workspace for the household.",
    bullets: ["Household Documents", "Family Obligations", "Shared Subscriptions", "Insurance",
              "Important Dates", "Shared Goals", "Assets", "Family Calendar", "Member Permissions"],
    disclaimer: "Dependent / minor financial access is deliberately gated on separate legal and privacy review.",
};

export const GOALS_PAGE = {
    statusKey: "shared_goals",
    hero: "Plan the important things together.",
    sub:  "Personal, couple or family goals — you set the target and the timeline, VAULT never recommends amounts.",
    bullets: ["Home", "Education", "Travel", "Vehicle", "Emergency Reserve", "Family Project"],
    disclaimer: "VAULT never recommends target amounts. This is organisational attribution only.",
};

export const PRIVACY_PAGE = {
    statusKey: "privacy",
    hero: "Sharing should never mean giving up privacy.",
    sub:  "Private · Shared Summary · Shared Full · Selected Members. Enforced server-side, on every read.",
    bullets: ["Private — only you", "Shared Summary — amounts contribute only",
              "Shared Full — fully visible", "Selected Members — only chosen members"],
    tabs: ["Mine", "Ours", "Private"],
};

export const CALENDAR_PAGE = {
    statusKey: "calendar",
    hero: "Your financial life has dates. VAULT remembers them.",
    sub:  "Payments, subscriptions, insurance, warranties, documents, EMIs and custom reminders — one calm forward view.",
    bullets: ["Payment", "Subscription", "Insurance", "Warranty", "Document", "EMI", "Custom Reminder"],
    cta: "See what's coming", ctaTo: "/signup",
};

export const INBOX_PAGE = {
    statusKey: "inbox",
    hero: "Stop wondering where it belongs.",
    sub:  "Add it to VAULT — Statement, Receipt, Policy, Invoice, Warranty, Investment Record or Loan Document.",
    bullets: ["VAULT Identifies", "VAULT Suggests", "You Review", "You Confirm"],
};

export const HEALTH_PAGE = {
    statusKey: "health",
    hero: "An organised financial life starts with knowing what needs attention.",
    sub:  "VAULT Health is a transparent score of how well organised your records are.",
    bullets: ["Records completeness", "Dates completeness", "Reminder configuration",
              "Security posture", "Reconciliation freshness"],
    disclaimer: "This is never a Credit Score, a Financial Health Score, or a Wealth Score.",
};

export const INTELLIGENCE_PAGE = {
    statusKey: "vault_intelligence",
    hero: "Your VAULT already has the information. Just ask.",
    sub:  "Natural-language retrieval of your own records — VAULT Intelligence organises and retrieves, it never recommends.",
    bullets: ["What needs attention?", "What is due this month?", "Which policies renew soon?",
              "Find my warranty.", "Summarise upcoming obligations."],
    disclaimer: "VAULT Intelligence organises and retrieves information. It does not provide regulated investment, tax or legal advice.",
};

export const ABOUT_PAGE = {
    hero: "We're building a simpler way to organise financial life.",
    sub:  "VAULT exists because financial information is increasingly scattered between accounts, applications, statements, emails, documents, subscriptions and family responsibilities. VAULT brings those pieces into one organised operating system.",
    bullets: ["One organised place", "Made for households", "Privacy-first",
              "Never a bank, adviser or lender", "Organise, not automate money"],
};

export const RESOURCES_CATEGORIES = [
    "Financial Organisation", "Family Finance Organisation", "Documents",
    "Subscriptions", "Financial Dates", "Product Guides", "VAULT Updates",
];
